import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/Card';
import { CalendarMonth, groupByMonth } from '../components/CalendarMonth';
import { Chip, type ChipTone } from '../components/Chip';
import { Nav } from '../components/Nav';
import { LoadingState } from '../components/LoadingState';
import {
  api,
  type DashboardPayload,
  type MealTemplate,
  type RangeDay,
} from '../lib/api';

// Calendar covers the full 35-week bet plus 1 week of pre-bet runway.
// Hard-coded to the bet window: 2026-04-27 (Mon before week 1) → 2026-12-31.
const RANGE_START = '2026-04-27';
const RANGE_END = '2026-12-31';

type View = 'calendar' | 'meals';

export default function Calendar() {
  const [days, setDays] = useState<RangeDay[] | null>(null);
  const [dash, setDash] = useState<DashboardPayload | null>(null);
  const [meals, setMeals] = useState<MealTemplate | null>(null);
  const [view, setView] = useState<View>('calendar');
  const [error, setError] = useState<string | null>(null);
  const [completedDates, setCompletedDates] = useState<Set<string>>(() => new Set());
  const [betStartDate, setBetStartDate] = useState<string | null>(null);

  const monthRefs = useRef<Map<string, HTMLElement>>(new Map());
  const todayRef = useRef<HTMLElement | null>(null);

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [d, dashData, completions, bet] = await Promise.all([
        api.getTrainingRange(RANGE_START, RANGE_END),
        api.getDashboard(),
        api.listTrainingCompletions(RANGE_START, RANGE_END).catch(() => ({ dates: [] as string[] })),
        api.getBet().catch(() => null),
      ]);
      setDays(d);
      setDash(dashData);
      setCompletedDates(new Set(completions.dates));
      setBetStartDate(bet?.starting_date ?? null);
      // Block A meals load immediately for the Meals subview default. If the
      // current block is B/C we'll re-fetch on toggle, but A is the common
      // case for the bet's first 12 weeks and saves a round-trip.
      const block = dashData.today.block;
      if (block !== 'B') {
        try {
          setMeals(await api.getMealTemplate(block));
        } catch {
          // non-fatal — Meals subview will retry
        }
      }
    } catch (e) {
      setError(String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Scroll today into view on first paint of the calendar list.
  useEffect(() => {
    if (!days || view !== 'calendar') return;
    const id = window.requestAnimationFrame(() => {
      todayRef.current?.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
    });
    return () => window.cancelAnimationFrame(id);
  }, [days, view]);

  const months = useMemo(() => (days ? groupByMonth(days) : []), [days]);

  // The day cell that should auto-scroll into view: today if it's in range,
  // otherwise the first month.
  const todayMonthKey = todayISO.slice(0, 7);

  const upcomingMonday = useMemo(() => nextMondayISO(todayISO), [todayISO]);

  if (!days || !dash) {
    return (
      <>
        <div className="min-h-full flex items-center justify-center text-fg-muted pb-32">
          {error ? (
            <pre className="text-danger text-sm whitespace-pre-wrap px-6">{error}</pre>
          ) : (
            <LoadingState />
          )}
        </div>
        <Nav />
      </>
    );
  }

  return (
    <>
      <div className="min-h-full max-w-md lg:max-w-5xl mx-auto px-5 lg:px-8 pt-6 lg:pt-10 pb-32">
        <header className="space-y-3 mb-4">
          <Link
            to="/dashboard"
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint hover:text-fg"
          >
            ← back
          </Link>
          <HeroStrip dash={dash} />
        </header>

        <Toolbar
          months={months}
          onJumpToday={() =>
            todayRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
          }
          onJumpMonday={() => {
            setView('meals');
            void refresh();
            const targetKey = upcomingMonday.slice(0, 7);
            window.requestAnimationFrame(() => {
              monthRefs.current
                .get(targetKey)
                ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            });
          }}
        />

        <div className="grid grid-cols-2 gap-1 my-4 p-1 rounded-full bg-bg-sunken">
          <ToggleBtn label="Calendar" active={view === 'calendar'} onClick={() => setView('calendar')} />
          <ToggleBtn label="Meals" active={view === 'meals'} onClick={() => setView('meals')} />
        </div>

        {view === 'calendar' ? (
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start">
            <div className="lg:col-span-2">
              <Legend />
            </div>
            {months.map((m) => (
              <CalendarMonth
                key={m.key}
                monthLabel={m.label}
                block={m.block}
                weekRange={m.weekRange}
                days={m.days}
                todayISO={todayISO}
                betStartDate={betStartDate}
                completedDates={completedDates}
                monthRef={(el) => {
                  if (el) monthRefs.current.set(m.key, el);
                  else monthRefs.current.delete(m.key);
                  if (m.key === todayMonthKey) todayRef.current = el;
                }}
              />
            ))}
          </div>
        ) : (
          <MealsSubview
            block={dash.today.block}
            meals={meals}
            setMeals={setMeals}
            days={days.filter((d) => d.date >= todayISO).slice(0, 7)}
          />
        )}
      </div>
      <Nav />
    </>
  );
}

function HeroStrip({ dash }: { dash: DashboardPayload }) {
  const { today, pace } = dash;
  const masking = dash.creatine_masking?.active ?? false;
  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <p className="font-display text-4xl leading-none tabular-nums">
            {today.days_to_deadline}
          </p>
          <p className="label-eyebrow">days to deadline</p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {masking ? (
            // Same read as the Dashboard hero: masking is a no-data state, so
            // it takes the quiet grey chip rather than the solid ink one.
            <Chip tone="muted" dot>
              Creatine water · {dash.creatine_masking?.day_of_load}/
              {dash.creatine_masking?.masking_days}
            </Chip>
          ) : (
            <Chip tone={paceTone(pace.status)} dot>
              {paceText(pace.status)}
            </Chip>
          )}
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
            {today.current_weight_kg !== null ? `${today.current_weight_kg.toFixed(1)} kg` : '—'}
            {' → '}
            {today.target_weight_today_kg !== null ? `${today.target_weight_today_kg} kg` : '—'}
          </p>
        </div>
      </div>
    </Card>
  );
}

function Toolbar({
  months,
  onJumpToday,
  onJumpMonday,
}: {
  months: { key: string; label: string }[];
  onJumpToday: () => void;
  onJumpMonday: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={onJumpToday}
          className="btn-secondary text-[11px] px-3.5 py-2 font-mono uppercase tracking-[0.14em]"
        >
          Today
        </button>
        <button
          type="button"
          onClick={onJumpMonday}
          className="btn-secondary text-[11px] px-3.5 py-2 font-mono uppercase tracking-[0.14em]"
        >
          Jump Mon ▸
        </button>
      </div>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
        {months.length} months
      </span>
    </div>
  );
}

function ToggleBtn({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs font-semibold py-2.5 rounded-full transition-colors ${
        active ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
      }`}
    >
      {label}
    </button>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-1.5">
      {[
        ['S', 'Squat'],
        ['H', 'Hinge'],
        ['U', 'Upper'],
        ['C', 'Cond.'],
        ['W', 'Walk'],
        ['·', 'Rest'],
      ].map(([letter, name]) => (
        <span
          key={letter}
          className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg-sunken px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted"
        >
          <span className="text-fg font-medium">{letter}</span>
          <span>{name}</span>
        </span>
      ))}
    </div>
  );
}

function MealsSubview({
  block,
  meals,
  setMeals,
  days,
}: {
  block: 'A' | 'B' | 'C';
  meals: MealTemplate | null;
  setMeals: (m: MealTemplate) => void;
  days: RangeDay[];
}) {
  // Re-fetch on demand if the user lands on Meals before a template was
  // pre-fetched (e.g. block changed, or initial load failed for this block).
  useEffect(() => {
    if (meals && meals.block === block) return;
    if (block === 'B') {
      setMeals({ block: 'B', step_target: 14000 });
      return;
    }
    api.getMealTemplate(block).then(setMeals).catch(() => undefined);
  }, [block, meals, setMeals]);

  if (!meals) {
    return (
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint py-8 text-center">
        Loading template…
      </p>
    );
  }

  if (block === 'B') {
    return (
      <div className="space-y-3">
        <Card eyebrow="Block B">
          <p className="text-sm text-fg leading-relaxed">
            No fixed meal template — log meals as you eat them. Stay above
            ~1.8 g/kg protein, target the daily kcal floor, and prep ahead when
            you can.
          </p>
        </Card>
        {days.map((d) => (
          <DayPrepRow key={d.date} day={d} block="B" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card eyebrow={`Block ${block} · Daily template`} hint={meals.description}>
        {meals.daily_totals && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Chip tone="muted">{meals.daily_totals.calories} kcal</Chip>
            <Chip tone="muted">{meals.daily_totals.protein_g}g protein</Chip>
            <Chip tone="muted">{meals.daily_totals.fat_g}g fat</Chip>
            <Chip tone="muted">{meals.step_target.toLocaleString()} steps</Chip>
          </div>
        )}
        {(meals.meals ?? []).map((m) => (
          <div key={m.id} className="rounded-panel border border-line bg-bg-sunken p-3 space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium text-fg">{m.label}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                {m.scheduled_time} · {m.calories} kcal
              </span>
            </div>
            <ul className="space-y-0.5">
              {m.ingredients.map((ing, i) => (
                <li key={i} className="text-sm text-fg-muted leading-snug">
                  · {ing.amount} {ing.item}
                </li>
              ))}
            </ul>
            {m.prep_notes && (
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-faint">
                {m.prep_notes}
              </p>
            )}
          </div>
        ))}
      </Card>

      <Card eyebrow="Next 7 days" hint="Today + 6">
        <ul className="space-y-1">
          {days.map((d) => (
            <DayPrepRow key={d.date} day={d} block={block} />
          ))}
        </ul>
        {meals.meals && meals.meals.length > 0 && (
          <GroceryButton meals={meals} dayCount={days.length} />
        )}
      </Card>
    </div>
  );
}

function DayPrepRow({ day, block }: { day: RangeDay; block: 'A' | 'B' | 'C' }) {
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day.weekday];
  const sameBlock = day.block === block;
  return (
    <li className="flex items-center justify-between gap-2 py-1.5 border-b last:border-b-0 border-line">
      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
        {wd} {day.date.slice(5)}
      </span>
      <span className="text-xs text-fg-faint">
        {sameBlock ? 'Daily template' : `Block ${day.block}`}
      </span>
    </li>
  );
}

function GroceryButton({ meals, dayCount }: { meals: MealTemplate; dayCount: number }) {
  const [open, setOpen] = useState(false);
  if (!meals.meals || meals.meals.length === 0) return null;

  // Sum identical ingredients across the template's meals × dayCount.
  const totals = new Map<string, number[]>();
  for (const meal of meals.meals) {
    for (const ing of meal.ingredients) {
      const key = ing.item.toLowerCase();
      const list = totals.get(key) ?? [];
      list.push(parseFirstNumber(ing.amount));
      totals.set(key, list);
    }
  }

  return (
    <div className="space-y-2 mt-3">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="btn-secondary w-full py-2.5 text-xs font-mono uppercase tracking-[0.14em]"
      >
        {open ? 'Hide grocery total' : `Show ${dayCount}-day grocery total`}
      </button>
      {open && (
        <ul className="space-y-1 pt-1">
          {Array.from(totals.entries()).map(([item, perMealAmounts]) => {
            const dailyTotal = perMealAmounts.reduce((a, b) => a + b, 0);
            const total = dailyTotal * dayCount;
            return (
              <li
                key={item}
                className="flex items-baseline justify-between gap-2 text-sm"
              >
                <span className="text-fg-muted capitalize">{item}</span>
                <span className="font-mono tabular-nums text-fg">
                  {total > 0 ? `${formatTotal(total)}` : '—'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function parseFirstNumber(s: string): number {
  const m = s.match(/[-+]?\d*\.?\d+/);
  return m ? parseFloat(m[0]) : 0;
}

function formatTotal(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

function nextMondayISO(fromISO: string): string {
  const d = new Date(`${fromISO}T00:00:00`);
  // Sun=0..Sat=6. Monday is 1. If today is Mon already, jump to next Mon.
  const wd = d.getDay();
  const diff = wd === 1 ? 7 : (8 - wd) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function paceTone(s: string): ChipTone {
  switch (s) {
    case 'on_pace':
      return 'good';
    case 'slow':
      return 'warn';
    case 'off_pace':
      return 'danger';
    default:
      return 'muted';
  }
}

function paceText(s: string): string {
  switch (s) {
    case 'on_pace':
      return 'On pace';
    case 'slow':
      return 'Slow';
    case 'off_pace':
      return 'Off pace';
    default:
      return 'No data';
  }
}
